<template>
  <div
    class="flex flex-col w-full h-full gap-y-2"
    :class="
      wizardMode
        ? 'min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white'
        : ''
    "
  >
    <div v-if="!wizardMode" class="border-b border-eblack-300">
      <ul
        class="flex flex-wrap -mb-px text-sm font-medium text-center text-eblack-950 bg-eblack-100/70 rounded-t-md pt-0.5 px-0.5"
      >
        <li class="me-2">
          <button
            type="button"
            @click="tab = 1"
            v-bind:class="[
              tab == 1
                ? 'bg-white border-b border-eorange-300'
                : 'border-transparent',
            ]"
            class="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 group"
          >
            <svg
              class="w-4 h-4 text-gray-400 me-2 group-hover:text-gray-500"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 18 20"
            >
              <path
                d="M16 1h-3.278A1.992 1.992 0 0 0 11 0H7a1.993 1.993 0 0 0-1.722 1H2a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2Zm-3 14H5a1 1 0 0 1 0-2h8a1 1 0 0 1 0 2Zm0-4H5a1 1 0 0 1 0-2h8a1 1 0 1 1 0 2Zm0-5H5a1 1 0 0 1 0-2h2V2h4v2h2a1 1 0 1 1 0 2Z"
              />
            </svg>{{ $t("admin.auto.formulario") }}</button>
        </li>
      </ul>
    </div>

    <FormTab
      v-if="tab == 1"
      :pItinerary="itinerarySelected"
      :initial-draft="initialDraft"
      :showCustomerModal="showCustomerModal"
      @form-updated="onFormUpdated"
      ref="form"
    />

    <div
      class="flex items-start gap-3"
      :class="
        wizardMode
          ? 'sticky bottom-0 z-10 mt-0 border-t border-gray-200 bg-white p-4'
          : 'mt-auto'
      "
    >
      <button
        type="button"
        class="px-4 py-1 text-white bg-green-500 rounded-lg"
        :class="[
          loading ? 'animate animate-bouncing' : '',
          isSaving ? 'opacity-50 cursor-not-allowed' : '',
        ]"
        :disabled="isSaving"
        @click="handleSubmit()"
      >
        {{ button_text }}
      </button>
      <div v-if="!isFormValid" class="ml-2 text-xs text-red-500">
        <p>
          {{ t("itinerary.form.form_required", "Por favor complete todos los campos requeridos") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import FormTab from "./FormTabs/FormTab.vue";
import itineraryRequest from "@/services/admin/itinerary-service";
import { calendarRepository } from "@domains/scheduling/repositories/calendar.repository";
import { useItineraryI18n } from "@/domains/itinerary/composables";
import { resolveApiErrorText } from "@/utils/api-error";
export default {
  props: [
    "itinerary",
    "initialDraft",
    "closeModal",
    "addItinerary",
    "handleShowCustomerModal",
    "wizardMode",
  ],
  name: "LayoutForm",
  emits: ["save-success"],
  setup() {
    const { t } = useItineraryI18n();
    return { t };
  },
  data() {
    return {
      tab: 1,
      button_text: "Guardar",
      itinerarySelected: null,
      isUpdating: false,
      toast: useToast(),
      loading: false,
      isSaving: false,
      currentFormData: null,
      isFormValid: false,
    };
  },
  watch: {
    itinerary: {
      handler(value) {
        this.itinerarySelected = null;
        this.isUpdating = false;
        if (value) {
          this.button_text = this.t("itinerary.actions.update", "Actualizar");
          this.itinerarySelected = value;
          this.isUpdating = true;
        } else {
          this.button_text = this.t("itinerary.actions.save", "Guardar");
        }
      },
      immediate: true,
    },
  },
  components: {
    FormTab,
  },
  methods: {
    onFormUpdated(formData) {
      this.currentFormData = formData;
      this.validateForm();
    },

    validateForm() {
      if (!this.currentFormData) {
        this.isFormValid = false;
        return;
      }

      this.isFormValid =
        this.currentFormData.customer_id > 0 &&
        this.currentFormData.itinerary_tour.length > 0 &&
        this.hasCompleteBookingItems(this.currentFormData);
    },
    hasCompleteBookingItems(form) {
      const items = form?.itinerary_tour ?? [];
      return items.every((item) => {
        return (
          Number(item.service_id) > 0 &&
          Boolean(item.start_datetime) &&
          Boolean(item.end_datetime) &&
          Number(item.quantity_of_participants) > 0 &&
          Number(item.price_per_service) > 0 &&
          Number(item.total) > 0
        );
      });
    },

    handleSubmit() {
      // Prevent duplicate submissions
      if (this.isSaving) {
        return;
      }

      if (!this.isFormValid) {
        this.toast.error(
          this.t(
            "itinerary.form.select_customer_service_error",
            "Debe seleccionar un cliente y al menos un servicio",
          ),
          {
            timeout: 3000,
            position: "top-right",
          },
        );
        return;
      }

      this.isSaving = true;
      this.loading = true;
      if (this.isUpdating) {
        this.button_text = this.t("itinerary.actions.updating", "Actualizando...");
        this.update();
      } else {
        this.button_text = this.t("itinerary.actions.saving", "Guardando...");
        this.save();
      }
    },
    async save() {
      let form = this.currentFormData || this.$refs.form.getFormData();

      if (!form || !form.customer_id || form.itinerary_tour.length === 0) {
        this.toast.error(
          this.t(
            "itinerary.form.form_incomplete",
            "Los datos del formulario están incompletos",
          ),
          {
            timeout: 3000,
            position: "top-right",
          },
        );
        this.loading = false;
        this.isSaving = false;
        this.button_text = this.t("itinerary.actions.save", "Guardar");
        return;
      }

      if (!this.hasCompleteBookingItems(form)) {
        this.toast.error(
          this.t(
            "itinerary.form.booking_items_required",
            "Debe seleccionar horarios disponibles y confirmar precios para todos los servicios",
          ),
          {
            timeout: 3000,
            position: "top-right",
          },
        );
        this.loading = false;
        this.isSaving = false;
        this.button_text = this.t("itinerary.actions.save", "Guardar");
        return;
      }

      // Pre-booking validation: Check availability for each booking item
      const bookingItems = form.booking_items || form.itinerary_tour || [];
      for (const item of bookingItems) {
        const date = item.date || item.start_datetime?.split("T")[0];
        const time =
          item.time || item.start_datetime?.split("T")[1]?.substring(0, 5);
        const participants = item.quantity_of_participants || 1;

        if (date && time && item.service_id) {
          try {
            const check = await calendarRepository.checkAvailability(
              item.service_id,
              date,
              time,
              participants,
            );

            if (!check.available) {
              this.toast.error(
                this.t(
                  "itinerary.form.no_availability",
                  {
                    reason:
                      check.reason ||
                      this.t("itinerary.form.no_capacity", "Sin capacidad"),
                  },
                  "No disponible: {reason}",
                ),
                {
                  timeout: 5000,
                  position: "top-right",
                },
              );
              this.loading = false;
              this.isSaving = false;
              this.button_text = this.t("itinerary.actions.save", "Guardar");
              return;
            }
          } catch (error) {
            console.error("[LayoutForm] Error validating availability:", error);
            // Continue with save if validation endpoint fails (graceful degradation)
          }
        }
      }

      let cleanForm = { ...form };
      delete cleanForm.start_date;
      delete cleanForm.end_date;
      // Remove calculated fields that server will compute
      delete cleanForm.commission_amount;
      delete cleanForm.extra_charge_total;
      delete cleanForm.tax_amount;
      delete cleanForm.subtotal;
      delete cleanForm.total;

      // Transform to booking_items for new API structure
      // booking_items is already prepared by FormTab.getFormData()
      // quantity_of_participants is now per-item, not global
      if (cleanForm.booking_items && Array.isArray(cleanForm.booking_items)) {
        cleanForm.booking_items = cleanForm.booking_items.map((item) => {
          const cleanItem = { ...item };
          // Keep only the fields needed for the new API
          return {
            service_id: cleanItem.service_id,
            start_datetime: cleanItem.start_datetime,
            end_datetime: cleanItem.end_datetime,
            quantity_of_participants: cleanItem.quantity_of_participants || 1,
            custom_field_values: cleanItem.custom_field_values || {},
            ...(cleanItem.notes ? { notes: cleanItem.notes } : {}),
          };
        });
      }

      // Remove the old itinerary_tour field as it's replaced by booking_items
      delete cleanForm.itinerary_tour;
      // Remove global quantity_of_participants - it's now per-item
      delete cleanForm.quantity_of_participants;

      itineraryRequest.save(cleanForm).then(
        (res) => {
          if (res.status == 201) {
            this.addItinerary(res.data);
            this.$emit("save-success"); // Notify parent that save was successful
            this.closeModal();
            this.toast.success(
              this.t(
                "itinerary.form.save_success",
                "Itinerario guardado correctamente",
              ),
              {
                timeout: 3000,
                position: "top-right",
              },
            );
            this.loading = false;
            this.isSaving = false;
            this.button_text = this.t("itinerary.actions.save", "Guardar");
          }
        },
        (error) => {
          const errorMessage = this.formErrorMessage(
            error,
            "itinerary.form.save_error",
            "Error al guardar el itinerario",
          );

          this.toast.error(errorMessage, {
            timeout: 5000,
            position: "top-right",
          });
          this.button_text = this.t("itinerary.actions.save", "Guardar");
          this.loading = false;
          this.isSaving = false;
        },
      );
    },
    async update() {
      let form = this.currentFormData || this.$refs.form.getFormData();

      if (!form || !form.customer_id) {
        this.toast.error(
          this.t(
            "itinerary.form.form_incomplete",
            "Los datos del formulario están incompletos",
          ),
          {
            timeout: 3000,
            position: "top-right",
          },
        );
        this.loading = false;
        this.isSaving = false;
        this.button_text = this.t("itinerary.actions.update", "Actualizar");
        return;
      }

      // Build per-slot notes update: only include slots that have an id (existing slots)
      const bookingItemNotes = (form.booking_items || [])
        .filter((item) => item.id)
        .map((item) => ({ id: item.id, notes: item.notes || null }));

      // Only send allowed fields for update
      const updatePayload = {
        customer_id: form.customer_id,
        tags: form.tags,
        ...(bookingItemNotes.length > 0 ? { booking_item_notes: bookingItemNotes } : {}),
      };

      itineraryRequest.update(this.itinerarySelected.id, updatePayload).then(
        (res) => {
          if (res.status == 200) {
            this.addItinerary(res.data, true);
            this.$emit("save-success"); // Notify parent that update was successful
            this.closeModal();
            this.toast.success(
              this.t(
                "itinerary.form.update_success",
                "Itinerario actualizado correctamente",
              ),
              {
                timeout: 3000,
                position: "top-right",
              },
            );
          }
          this.loading = false;
          this.isSaving = false;
          this.button_text = this.t("itinerary.actions.update", "Actualizar");
        },
        (error) => {
          const errorMessage = this.formErrorMessage(
            error,
            "itinerary.form.update_error",
            "Error al actualizar el itinerario",
          );

          this.toast.error(errorMessage, {
            timeout: 5000,
            position: "top-right",
          });
          this.loading = false;
          this.isSaving = false;
          this.button_text = this.t("itinerary.actions.update", "Actualizar");
        },
      );
    },
    formErrorMessage(error, fallbackKey, fallback) {
      return resolveApiErrorText(
        error,
        (key, variables) => this.t(key, variables),
        this.t(fallbackKey, fallback),
      );
    },
    showCustomerModal(value) {
      this.handleShowCustomerModal(value);
    },
    setCustomerSelected(customer) {
      this.$refs.form.addNewCustomer(customer);
    },
  },
};
</script>

<style lang="scss" scoped></style>
